import { useNavigate } from 'react-router-dom'

import { todayIso } from '../lib/format'
import { newId } from '../lib/ids'
import { KINDS, titleField, type EntityKind } from '../lib/model'
import { useStore } from '../lib/store'

/** The starting field values for a new record of each kind, so a record is never
 *  born without the labels its views filter on. */
export function defaultsFor(
  kind: EntityKind,
  stages: { key: string; kind: string }[],
  projectKinds: { key: string }[],
): Record<string, unknown> {
  switch (kind) {
    case 'deal':
      // A new deal starts at the first open stage — which is `idea`, so "what if
      // we sold X to Y" has somewhere to land without inventing a second table.
      return { stage: stages.find((s) => s.kind === 'open')?.key ?? stages[0]?.key ?? 'idea' }
    case 'project':
      return {
        kind: projectKinds[0]?.key ?? 'demo',
        // "available" is the shelf state that means "this exists and isn't set aside".
        state: 'available',
        start: todayIso(),
      }
    case 'org':
      return { state: 'active' }
    default:
      return {}
  }
}

/**
 * Create a record of a kind with a title, link it to whatever it was created
 * from, then open its detail page.
 *
 * `links` is what makes creating IN CONTEXT mean anything. Creating a project from
 * a client's page and landing on an unattached project is barely better than the
 * New button on the list, because the one fact you had — whose it is — is the one
 * thing that got dropped. Fields alone could not carry it: a client is a link, not
 * a field, deliberately (see the `client` field crum removed).
 *
 * `opts.stay` skips the navigation, for the places where you are adding several
 * things to a list you want to keep looking at.
 */
export function useCreateEntity() {
  const { dispatch, config } = useStore()
  const navigate = useNavigate()
  return async function create(
    kind: EntityKind,
    name: string,
    extra?: Record<string, unknown>,
    opts?: { links?: string[]; stay?: boolean },
  ): Promise<string> {
    const clean = name.trim()
    if (!clean) return ''
    const id = newId(KINDS[kind].prefix)
    await dispatch([
      {
        type: `${kind}.create`,
        subject: id,
        data: {
          [titleField(kind)]: clean,
          ...defaultsFor(kind, config.stages, config.projectKinds),
          ...extra,
        },
      },
      ...(opts?.links ?? []).map((to) => ({
        type: 'link.add',
        subject: id,
        data: { to },
      })),
    ])
    if (!opts?.stay) navigate(`/e/${id}`)
    return id
  }
}
